import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, X, Lock } from "lucide-react";
import { Seo } from "@/components/Seo";
import { IMAGES } from "@/lib/content";
import { track } from "@/lib/analytics";

/**
 * /preview — Reader Preview drawing verbatim from the Employee_2_Employer_The_Shift_Customer_Ready
 * manuscript. Content flow: Cover → Prayer → Scripture → Introduction (3 pages) →
 * Chapter 1 (3 pages) → Preview lock with $19 CTA.
 *
 * Short paragraphs from the manuscript are preserved as separate <p> elements —
 * never combined into blocks. Don's conversational grammar (ain't, yo, outta,
 * fragment sentences) is untouched.
 */

/* ============================== MANUSCRIPT ============================== */

const PRAYER = [
  "Heavenly Father,",
  "Thank You for every person holding this book.",
  "You know their story even before they begin reading these pages.",
  "Some are discouraged.",
  "Some are searching for direction.",
  "Some are carrying dreams they've been too afraid to pursue.",
  "Some are simply tired.",
  "Lord, I pray this book becomes more than words on paper.",
  "I pray it becomes a tool You use to renew minds, strengthen faith, and awaken purpose.",
  "Give each reader wisdom to recognize the gifts You've placed inside them.",
  "Give them courage to take the first step, even when they're afraid.",
  "Give them discipline to stay faithful when the excitement wears off.",
  "Protect them from chasing success without chasing You.",
  "Remind them that their identity isn't found in a job title, a paycheck, or a business.",
  "Their identity is found in You.",
  "Teach them to build with integrity.",
  "Teach them to serve with humility.",
  "Teach them to lead with love.",
  "And whatever they build, may it point people back to Jesus Christ.",
  "Thank You for allowing me to be a small part of their journey.",
  "May everything in this book bring You glory.",
  "In Jesus' name,",
  "Amen.",
];

const SCRIPTURE_INTRO = [
  "This verse became the foundation of everything you're about to read.",
  "Because the goal of this book isn't simply to teach you how to build a business.",
  "It's to teach you how to build a life on the only foundation that lasts.",
  "Without God...",
  "Success eventually feels empty.",
  "Money eventually runs out.",
  "Titles eventually fade.",
  "But when God is your foundation...",
  "Everything you build has purpose.",
  "As you read this book, I want you to remember one thing:",
];

const INTRO_1 = [
  "Let me ask you something.",
  "Have you ever just sat back and thought...",
  "\u201CIs this really it?\u201D",
  "Wake up.",
  "Go to work.",
  "Come home.",
  "Pay bills.",
  "Go to sleep.",
  "Then wake up and do it all over again.",
  "Day after day.",
  "Year after year.",
  "Now don't get me wrong.",
  "There ain't nothing wrong with working.",
  "Matter of fact, I believe in working hard.",
  "I've been working ever since I was old enough to get a job.",
  "But somewhere along the way...",
  "I started asking myself a different question.",
  "\u201CWhat am I really building?\u201D",
  "Because I realized something.",
  "You can work hard every single day...",
  "And still feel like something missing.",
  "Like God put something bigger in yo heart...",
  "But you just can't figure out what it is.",
  "I know that feeling.",
  "I've lived it.",
  "For the longest...",
  "I thought if I just worked harder...",
  "Everything would eventually work out.",
  "Make more money.",
  "Get a better opportunity.",
  "Keep grinding.",
  "But the harder I worked...",
  "The more I realized I was making a living...",
  "I wasn't really building a life.",
  "There's a difference.",
  "And once I saw it...",
  "I couldn't unsee it.",
];

const INTRO_2 = [
  "One day I heard a quote from Zig Ziglar.",
  "He said,",
  { quote: "You can have everything in life you want if you help enough other people get what they want." },
  "Man...",
  "That quote hit different.",
  "Because if I'm being honest...",
  "Most of my thinking was about me.",
  "How do I make more money?",
  "How do I get ahead?",
  "How do I build the life I want?",
  "God used that quote to check me.",
  "Instead of asking...",
  "\u201CWhat's in it for me?\u201D",
  "I started asking...",
  "\u201CWho can I help?\u201D",
  "That one question changed the way I looked at everything.",
  "That's really why I wrote this book.",
  "Not because I got everything figured out.",
  "I don't.",
  "I still need God every single day.",
  "I still make mistakes.",
  "I still learn.",
  "I still grow.",
  "But I learned some lessons that changed my life.",
  "And I believe they'll help change yours too.",
];

const INTRO_3 = [
  "Before we go any further...",
  "I want to clear something up.",
  "This book ain't about telling everybody to quit they job.",
  "If you love what you do...",
  "Keep showing up.",
  "Work hard.",
  "Honor God.",
  "Serve people.",
  "Be the best at it.",
  "There ain't nothing wrong with that.",
  "This book is about something deeper.",
  "It's about the way we think.",
  "Because you can own a business...",
  "And still think like an employee.",
  "And you can have a job...",
  "While building something that'll change yo life and the people around you.",
  "See the difference?",
  "This book ain't about a job.",
  "It's about a mindset.",
  "Here's what I believe.",
  "God didn't create you just to survive.",
  "He created you to build.",
  "Maybe that's building a family.",
  "Maybe it's building a business.",
  "Maybe it's building a ministry.",
  "Maybe it's building stronger finances.",
  "Maybe it's helping people in a way only you can.",
  "Whatever it is...",
  "Don't ignore it.",
  "Because the world don't need another person just going through the motions.",
  "The world need people building what God put in they heart.",
];

const INTRO_4 = [
  "As you read this book...",
  "Don't rush through it.",
  "Think about it.",
  "Answer the questions.",
  "Do the challenges.",
  "Pray.",
  "Ask God to show you what He wants you to see.",
  "Because just reading this book won't change yo life.",
  "Applying it will.",
  "Before you turn the page...",
  "I want you to answer one question.",
  "And don't lie to yourself.",
  "Five years from now...",
  "If yo life looked exactly the way it do today...",
  "Would you honestly be happy?",
  "If the answer is yes...",
  "I'm happy for you.",
  "Keep building.",
  "But if the answer is no...",
  "Don't wait another five years.",
  "Start today.",
  "One decision.",
  "One step.",
  "One act of faith.",
  "That's all it takes to get started.",
  "I'm glad you're here.",
  { emphasis: "Now let's build." },
  { emphasis: "Keep building." },
  { emphasis: "Stay faithful." },
  { emphasis: "Let God handle the harvest." },
];

const CH1_1 = [
  { quote: "Most people spend they whole life working hard. Very few stop and ask what they're building." },
  "Have you ever just sat back...",
  "And asked yourself...",
  "\u201CIs this really it?\u201D",
  "Not because you ungrateful.",
  "Not because you don't appreciate what God has already done.",
  "But because something inside you keeps telling you...",
  "There's more.",
  "More than waking up every morning just to rush out the door.",
  "More than counting down until Friday.",
  "More than living for the weekend.",
  "More than paying bills...",
  "Just to get another bill next month.",
  "If you've ever felt that way...",
  "I want you to know something.",
  "You ain't alone.",
  "Matter of fact...",
  "I think millions of people feel that way.",
  "Most of them just don't say it out loud.",
  "Because somewhere along the way...",
  "We learned that asking questions like that makes us sound ungrateful.",
  "So we keep quiet.",
  "We keep smiling.",
  "We keep showing up.",
  "And deep down...",
  "We keep wondering if this is really what life is supposed to be.",
];

const CH1_2 = [
  "Think about when you was growing up.",
  "What did success look like?",
  "I'm willing to bet it sounded something like this.",
  "Go to school.",
  "Get good grades.",
  "Stay outta trouble.",
  "Get a good job.",
  "Work hard.",
  "Keep your head down.",
  "Retire one day.",
  "Then enjoy your life.",
  "Sound familiar?",
  "That was the blueprint.",
  "At least that's the one I was given.",
  "Maybe it was the one you was given too.",
  "Now before we go any further...",
  "I need you to hear me.",
  "There ain't nothing wrong with education.",
  "There ain't nothing wrong with having a job.",
  "There definitely ain't nothing wrong with working hard.",
  "I believe in hard work.",
  "Always will.",
  "God created us to work.",
  "Work ain't the problem.",
  "The problem is...",
  "Somewhere along the way...",
  "We started believing hard work by itself would automatically build the life we wanted.",
  { emphasis: "That's the lie." },
  "Not because hard work don't matter.",
  "Because hard work without direction can leave you exhausted.",
  "Hard work without purpose can leave you empty.",
  "And hard work without ownership can leave you wondering where all the years went.",
];

const CH1_3 = [
  "One day...",
  "God challenged me with one simple question.",
  "Not through a loud voice.",
  "Not through some crazy experience.",
  "Just one question that wouldn't leave me alone.",
  { emphasis: "\u201CWhat are you building?\u201D" },
  "Man...",
  "That question messed me up.",
  "Because I had answers for everything else.",
  "I knew where I worked.",
  "I knew how much money I wanted to make.",
  "I knew what I wanted to buy.",
  "I knew what I wanted my life to look like.",
  "But I couldn't answer that question.",
  "What...",
  "Am...",
  "I...",
  "Building?",
  "Not what am I earning.",
  "Not what am I doing.",
  "Not what title do I have.",
  { emphasis: "What am I building?" },
  "There's a difference.",
  "A big difference.",
  "The more I thought about it...",
  "The more I realized something.",
  "I had spent years making a living.",
  "But I wasn't intentionally building a life.",
  "Read that again.",
  "I was making a living.",
  "I wasn't building a life.",
];

const CH1_4_STOP = {
  intro: [
    "Those two things ain't always the same.",
    "You can make good money...",
    "And still not be building anything that'll outlive you.",
    "You can stay busy every single day...",
    "And still not be moving toward the life God put in your heart.",
    "Being busy don't always mean you're making progress.",
    "Sometimes...",
    "It just means you're tired.",
  ],
  prompt: [
    "If somebody asked you today...",
    "\u201CWhat are you building?\u201D",
    "Could you answer them?",
    "Not where you work.",
    "Not how much money you make.",
    "What are you building?",
  ],
};

const CH1_5 = [
  "The crazy part is...",
  "Most of us never ask ourselves that question.",
  "Not because we don't care.",
  "Because nobody ever taught us to.",
  "We were taught how to get a job.",
  "We were taught how to interview.",
  "How to write a résumé.",
  "How to be on time.",
  "How to follow instructions.",
  "How to work for somebody else's vision.",
  { emphasis: "But who taught us how to discover God's vision for our own life?" },
  { emphasis: "Who taught us how to recognize the gifts He placed inside us?" },
  { emphasis: "Who taught us that serving people creates value... and value creates opportunity?" },
];

/* =============================== PAGES =============================== */

const PAGES = [
  { id: "cover", kind: "cover" },
  { id: "dedication", kind: "dedication" },
  { id: "prayer", kind: "front", label: "A Prayer Before We Begin", body: PRAYER },
  {
    id: "scripture",
    kind: "scripture",
    label: "Foundational Scripture",
    verse: "Unless the Lord builds the house, the builders labor in vain.",
    citation: "Psalm 127:1",
    body: SCRIPTURE_INTRO,
    closer: {
      lines: [
        "Don't just ask God to bless what you're building.",
        "Build what God can bless.",
      ],
    },
  },
  { id: "intro-title", kind: "sectionTitle", pretitle: "Introduction", title: "From Don" },
  { id: "intro-1", kind: "chapter", label: "Introduction", body: INTRO_1 },
  { id: "intro-2", kind: "chapter", label: "Introduction", body: INTRO_2 },
  { id: "intro-3", kind: "chapter", label: "Introduction", body: INTRO_3 },
  { id: "intro-4", kind: "chapter", label: "Introduction", body: INTRO_4 },
  { id: "ch1-title", kind: "chapterTitle", number: "1", title: "The Lie We've Been Sold" },
  { id: "ch1-1", kind: "chapter", label: "Chapter 1", body: CH1_1 },
  { id: "ch1-2", kind: "chapter", label: "Chapter 1", body: CH1_2 },
  { id: "ch1-3", kind: "chapter", label: "Chapter 1", body: CH1_3 },
  { id: "ch1-4", kind: "stopThink", label: "Chapter 1", ...CH1_4_STOP },
  { id: "ch1-5", kind: "chapter", label: "Chapter 1", body: CH1_5 },
  { id: "lock", kind: "lock" },
];

/* ============================== COMPONENT ============================== */

export default function ReaderPreview() {
  const [i, setI] = useState(0);
  const navigate = useNavigate();
  const total = PAGES.length;
  const page = PAGES[i];
  const progressPct = ((i + 1) / total) * 100;

  const prev = useCallback(() => setI((n) => Math.max(0, n - 1)), []);
  const next = useCallback(() => setI((n) => Math.min(total - 1, n + 1)), [total]);
  const close = useCallback(() => navigate("/"), [navigate]);

  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight" || e.key === " ") next();
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prev, next, close]);

  useEffect(() => {
    document.getElementById("reader-frame")?.scrollTo?.({ top: 0, behavior: "smooth" });
    window.scrollTo?.({ top: 0, behavior: "smooth" });
  }, [i]);

  return (
    <div className="bg-[#faf7f1] text-black min-h-screen" data-testid="preview-page">
      <Seo
        title="Read The Shift — Advance Reader Preview"
        description="Read the opening pages of The Shift by Don Orr Jr. — the prayer, the scripture, the introduction, and the beginning of Chapter One."
        path="/preview"
      />

      {/* Header */}
      <div className="sticky top-0 z-30 bg-[#faf7f1]/95 backdrop-blur-md border-b border-black/8">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 pt-24 pb-4 lg:pt-28 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="font-body text-[10px] tracking-[0.28em] uppercase text-black/50">
              Advance Reader Preview
            </p>
            <p className="font-display text-lg lg:text-xl text-black leading-tight truncate mt-0.5" data-testid="reader-current-label">
              {page.label || page.title || "The Shift"}
            </p>
          </div>
          <button
            onClick={close}
            data-testid="reader-close"
            aria-label="Close preview"
            className="inline-flex items-center gap-2 text-black/60 hover:text-black font-body text-sm px-3 py-2 transition-colors"
          >
            <X size={16} /> <span className="hidden sm:inline">Close Preview</span>
          </button>
        </div>
        <div className="h-[3px] bg-black/8 relative">
          <div
            className="absolute inset-y-0 left-0 bg-[#B8952E] transition-all duration-500"
            style={{ width: `${progressPct}%` }}
            data-testid="reader-progress-bar"
          />
        </div>
      </div>

      <div
        id="reader-frame"
        className="max-w-3xl mx-auto px-6 lg:px-10 py-12 lg:py-20 min-h-[70vh]"
        data-testid="reader-frame"
      >
        <PageRenderer page={page} />
      </div>

      {/* Nav */}
      <nav
        className="sticky bottom-0 z-30 bg-[#faf7f1]/95 backdrop-blur-md border-t border-black/8"
        data-testid="reader-nav"
        aria-label="Reader navigation"
      >
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-4 flex items-center justify-between gap-4">
          <button
            onClick={prev}
            disabled={i === 0}
            data-testid="reader-prev"
            aria-label="Previous page"
            className="inline-flex items-center gap-2 font-body text-sm text-black/70 hover:text-[#B8952E] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowLeft size={16} /> Previous
          </button>
          <span
            className="font-body text-xs tracking-[0.2em] uppercase text-black/45"
            data-testid="reader-page-indicator"
          >
            {i + 1} / {total}
          </span>
          {page.kind === "lock" ? (
            <Link
              to="/checkout"
              onClick={() => track.cta("get_the_shift_from_lock", "preview_lock_nav")}
              data-testid="reader-nav-get-shift"
              className="inline-flex items-center gap-2 bg-[#111] hover:bg-[#B8952E] text-white font-body font-medium text-sm px-4 py-2 transition-colors"
            >
              Get The Shift — $19
            </Link>
          ) : (
            <button
              onClick={next}
              disabled={i === total - 1}
              data-testid="reader-next"
              aria-label="Next page"
              className="inline-flex items-center gap-2 font-body text-sm text-black hover:text-[#B8952E] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Next <ArrowRight size={16} />
            </button>
          )}
        </div>
      </nav>
    </div>
  );
}

/* =========================== Renderers =========================== */

function PageRenderer({ page }) {
  switch (page.kind) {
    case "cover":
      return <CoverPage />;
    case "dedication":
      return <DedicationPage />;
    case "front":
      return <FrontMatterPage page={page} />;
    case "scripture":
      return <ScripturePage page={page} />;
    case "sectionTitle":
      return <SectionTitlePage page={page} />;
    case "chapterTitle":
      return <ChapterTitlePage page={page} />;
    case "chapter":
      return <ProsePage page={page} />;
    case "stopThink":
      return <StopThinkPage page={page} />;
    case "lock":
      return <LockPage />;
    default:
      return null;
  }
}

function Line({ item, base = "text-[17px] lg:text-lg leading-[1.85] text-black/85" }) {
  if (typeof item === "string")
    return <p className={`font-body ${base}`}>{item}</p>;
  if (item.quote)
    return (
      <blockquote className="border-l-2 border-[#B8952E] pl-5 py-1 font-display italic text-xl lg:text-2xl text-black leading-[1.45]">
        &ldquo;{item.quote}&rdquo;
      </blockquote>
    );
  if (item.emphasis)
    return (
      <p className={`font-display italic text-xl lg:text-2xl text-black leading-snug`}>
        {item.emphasis}
      </p>
    );
  return null;
}

function DedicationPage() {
  return (
    <div className="fade-in text-center py-24 lg:py-40" data-testid="reader-dedication">
      <p className="font-body text-[11px] tracking-[0.32em] uppercase text-[#B8952E]">
        Dedication
      </p>
      <div className="w-16 h-px bg-[#B8952E] mx-auto mt-8" />
    </div>
  );
}


function CoverPage() {
  return (
    <div className="text-center py-8 lg:py-16 fade-in" data-testid="reader-cover">
      <img
        src={IMAGES.book}
        alt="The Shift"
        className="max-h-[440px] w-auto mx-auto"
        style={{ filter: "drop-shadow(0 40px 30px rgba(0,0,0,0.28))" }}
      />
      <p className="font-body text-[11px] tracking-[0.32em] uppercase text-black/55 mt-12">
        Employee 2 Employer
      </p>
      <h1 className="font-display text-5xl lg:text-6xl mt-4 tracking-tight">The Shift</h1>
      <p className="font-display italic text-xl text-black/70 mt-4 leading-snug max-w-md mx-auto">
        Break the Employee Mindset.
        <br />
        Build the Life God Created You For.
      </p>
      <div className="mt-14 flex items-center justify-center gap-3">
        <div className="w-10 h-px bg-[#B8952E]" />
        <span className="font-body text-xs tracking-[0.28em] uppercase text-black/60">
          Don Orr Jr.
        </span>
        <div className="w-10 h-px bg-[#B8952E]" />
      </div>
      <p className="font-body text-[11px] tracking-[0.28em] uppercase text-black/40 mt-8">
        Advance Reader Preview
      </p>
    </div>
  );
}

function FrontMatterPage({ page }) {
  return (
    <article className="fade-in max-w-xl mx-auto">
      <p className="font-body text-xs tracking-[0.28em] uppercase text-[#B8952E]">{page.label}</p>
      <div className="w-10 h-px bg-[#B8952E]/70 mt-3" />
      <div className="mt-10 space-y-5">
        {page.body.map((item, k) => (
          <Line key={k} item={item} />
        ))}
      </div>
    </article>
  );
}

function ScripturePage({ page }) {
  return (
    <article className="fade-in max-w-xl mx-auto">
      <p className="font-body text-xs tracking-[0.28em] uppercase text-[#B8952E]">{page.label}</p>
      <div className="w-10 h-px bg-[#B8952E]/70 mt-3" />
      <blockquote className="mt-12 mb-12 text-center">
        <p className="font-display italic text-2xl lg:text-3xl text-black leading-[1.35] max-w-lg mx-auto">
          &ldquo;{page.verse}&rdquo;
        </p>
        <p className="font-body text-sm tracking-[0.22em] uppercase text-black/50 mt-6">
          — {page.citation}
        </p>
      </blockquote>
      <div className="space-y-5">
        {page.body.map((item, k) => (
          <Line key={k} item={item} />
        ))}
      </div>
      {page.closer && (
        <div className="mt-10 border-t border-[#B8952E]/40 pt-8 space-y-2">
          {page.closer.lines.map((l, k) => (
            <p key={k} className="font-display italic text-xl lg:text-2xl text-black leading-snug">
              {l}
            </p>
          ))}
        </div>
      )}
    </article>
  );
}

function SectionTitlePage({ page }) {
  return (
    <div className="fade-in text-center py-16 lg:py-28">
      <p className="font-body text-xs tracking-[0.32em] uppercase text-[#B8952E]">{page.pretitle}</p>
      <div className="w-16 h-px bg-[#B8952E] mx-auto mt-6" />
      <h2 className="font-display text-4xl sm:text-5xl mt-10 leading-[1.06] tracking-tight max-w-2xl mx-auto">
        {page.title}
      </h2>
    </div>
  );
}

function ChapterTitlePage({ page }) {
  return (
    <div className="fade-in text-center py-16 lg:py-24">
      <p className="font-body text-xs tracking-[0.32em] uppercase text-[#B8952E]">Chapter {page.number}</p>
      <div className="w-16 h-px bg-[#B8952E] mx-auto mt-6" />
      <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-10 leading-[1.06] tracking-tight max-w-2xl mx-auto">
        {page.title}
      </h2>
    </div>
  );
}

function ProsePage({ page }) {
  return (
    <article className="fade-in max-w-xl mx-auto">
      <p className="font-body text-xs tracking-[0.28em] uppercase text-[#B8952E]">{page.label}</p>
      <div className="w-10 h-px bg-[#B8952E]/70 mt-3" />
      <div className="mt-10 space-y-5">
        {page.body.map((item, k) => (
          <Line key={k} item={item} />
        ))}
      </div>
    </article>
  );
}

function StopThinkPage({ page }) {
  return (
    <article className="fade-in max-w-xl mx-auto">
      <p className="font-body text-xs tracking-[0.28em] uppercase text-[#B8952E]">{page.label}</p>
      <div className="w-10 h-px bg-[#B8952E]/70 mt-3" />
      <div className="mt-10 space-y-5">
        {page.intro.map((s, k) => (
          <p key={k} className="font-body text-[17px] lg:text-lg leading-[1.85] text-black/85">
            {s}
          </p>
        ))}
      </div>
      <div className="mt-14 border border-[#B8952E]/60 bg-white p-8 lg:p-10">
        <p className="font-body text-[11px] tracking-[0.32em] uppercase text-[#B8952E]">
          Stop &amp; Think
        </p>
        <div className="mt-6 space-y-3">
          {page.prompt.map((s, k) => (
            <p
              key={k}
              className="font-display italic text-xl lg:text-2xl text-black leading-snug"
            >
              {s}
            </p>
          ))}
        </div>
      </div>
    </article>
  );
}

function LockPage() {
  return (
    <div className="fade-in relative pt-8 lg:pt-16" data-testid="reader-lock">
      {/* Premium lock card */}
      <div className="max-w-lg mx-auto text-center border border-[#B8952E]/40 bg-white p-10 lg:p-14 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.35)]">
        <div className="mx-auto w-14 h-14 border border-[#B8952E] rounded-full flex items-center justify-center">
          <Lock size={20} className="text-[#B8952E]" strokeWidth={1.4} />
        </div>
        <p className="font-body text-[11px] tracking-[0.32em] uppercase text-[#B8952E] mt-8">
          Keep Building.
        </p>
        <h2 className="font-display text-3xl lg:text-4xl mt-5 leading-[1.1] tracking-tight">
          You&rsquo;ve started <span className="italic">The Shift.</span>
        </h2>
        <p className="font-display italic text-lg text-black/70 mt-4">
          Now finish what you started.
        </p>
        <p className="font-body text-black/60 mt-8 leading-relaxed">
          Continue reading <em>Employee 2 Employer: The Shift</em> and begin building the life
          God created you for.
        </p>
        <Link
          to="/checkout"
          onClick={() => track.cta("get_the_shift_from_lock", "preview_lock")}
          data-testid="reader-lock-get-shift"
          data-track="cta_click"
          data-track-label="get_the_shift"
          data-track-location="preview_lock"
          className="mt-10 inline-flex items-center gap-3 bg-[#111] hover:bg-[#B8952E] text-white font-body font-medium text-[15px] px-10 py-4 transition-colors duration-300"
        >
          Get The Shift — $19 <ArrowRight size={16} />
        </Link>
        <p className="font-body text-xs text-black/45 mt-5">Instant digital access.</p>
      </div>

      <p className="font-body text-center text-xs text-black/40 mt-10">
        &mdash; Don Orr Jr.
      </p>
    </div>
  );
}
